// Employees Management Page: List, view, edit, delete, and add employees
import React, { useState, useEffect } from 'react';
import { Eye, Edit, Trash, Plus, X } from 'lucide-react';
import { getEmployees, registerEmployeeService, updateEmployeeService, deleteEmployeeService } from '../../services/MasterAdmin/EmployeeManagementService';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const roleOptions = [
  'MASTER_ADMIN',
  'SALES_EXECUTIVE',
  'ACCOUNTANT',
  'WRITER',
  'MARKETING',
  'SUPPORT',
  'TECH',
  'HR',
];

const departmentOptions = [
  'Management',
  'Sales',
  'Support',
  'Tech',
  'HR',
  'Operations',
  'Finance',
  'Marketing',
  'Legal',
  'Admin',
];

const EmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: roleOptions[0],
    department: departmentOptions[0],
    phone: '',
    qualification: '',
  });
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [removeProfileImage, setRemoveProfileImage] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editEmployeeId, setEditEmployeeId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<any | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const response = await getEmployees();
        if (response.data && response.data.employees) {
          setEmployees(response.data.employees);
        } else {
          setEmployees([]);
        }
      } catch (err) {
        setToast('Failed to fetch employees.');
        setTimeout(() => setToast(''), 2500);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const handleView = (emp: typeof employees[0]) => {
    if (emp.role === 'SALES_EXECUTIVE') {
      navigate(`/employees/sales-executive/${emp.id}`);
      return;
    }
    navigate(`/employees/member/${emp.id}`);
  };

  const handleEdit = (emp: typeof employees[0]) => {
    setModalMode('edit');
    setEditEmployeeId(emp.id);
    setForm({
      name: emp.name || '',
      email: emp.email || '',
      password: '',
      role: emp.role || roleOptions[0],
      department: emp.department || departmentOptions[0],
      phone: emp.phone || '',
      qualification: emp.qualification || '',
    });
    setProfilePreview(emp.profile_image || null);
    setProfileImageFile(null);
    setRemoveProfileImage(false);
    setShowAddModal(true);
  };

  const handleDelete = (emp: typeof employees[0]) => {
    setEmployeeToDelete(emp);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!employeeToDelete) return;
    try {
      const response = await deleteEmployeeService(employeeToDelete.id);
      if (response.data && response.data.message) {
        setEmployees(employees.filter(e => e.id !== employeeToDelete.id));
        setToast('Employee deleted successfully!');
        setTimeout(() => setToast(''), 2500);
      } else {
        setToast('Failed to delete employee.');
        setTimeout(() => setToast(''), 2500);
      }
    } catch (err: any) {
      setToast(err?.response?.data?.message || 'Failed to delete employee.');
      setTimeout(() => setToast(''), 2500);
    } finally {
      setShowDeleteModal(false);
      setEmployeeToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
  };

  const handleAdd = () => {
    setModalMode('add');
    setEditEmployeeId(null);
    setForm({
      name: '',
      email: '',
      password: '',
      role: roleOptions[0],
      department: departmentOptions[0],
      phone: '',
      qualification: '',
    });
    setProfilePreview(null);
    setProfileImageFile(null);
    setRemoveProfileImage(false);
    setShowAddModal(true);
  };

  const handleModalClose = () => {
    if (profilePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(profilePreview);
    }
    setShowAddModal(false);
    setForm({
      name: '',
      email: '',
      password: '',
      role: roleOptions[0],
      department: departmentOptions[0],
      phone: '',
      qualification: '',
    });
    setProfilePreview(null);
    setProfileImageFile(null);
    setRemoveProfileImage(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setToast('Profile image must be 2MB or smaller.');
      setTimeout(() => setToast(''), 2500);
      return;
    }
    if (profilePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(profilePreview);
    }
    setProfileImageFile(file);
    setRemoveProfileImage(false);
    setProfilePreview(URL.createObjectURL(file));
  };

  const clearProfileImage = () => {
    if (profilePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(profilePreview);
    }
    setProfileImageFile(null);
    setRemoveProfileImage(true);
    setProfilePreview(null);
  };

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setToast('You must be logged in as Master Admin to add employees.');
      setTimeout(() => setToast(''), 2500);
      return;
    }
    if (modalMode === 'add') {
      try {
        const { password, qualification, ...rest } = form;
        const response = await registerEmployeeService(
          {
            ...rest,
            password,
            qualification: qualification.trim(),
            created_by: user.id,
          },
          { profileImageFile }
        );
        if (response.data && response.data.user) {
          setEmployees(prev => [...prev, response.data.user]);
          setShowAddModal(false);
          setToast('Employee added successfully!');
          setTimeout(() => setToast(''), 2500);
          handleModalClose();
        } else {
          setToast('Failed to add employee.');
          setTimeout(() => setToast(''), 2500);
        }
      } catch (err: any) {
        setToast(err?.response?.data?.message || 'Failed to add employee.');
        setTimeout(() => setToast(''), 2500);
      }
    } else if (modalMode === 'edit' && editEmployeeId) {
      try {
        const { password, qualification, ...rest } = form;
        const response = await updateEmployeeService(
          editEmployeeId,
          {
            ...rest,
            qualification: qualification.trim(),
            created_by: user.id,
          },
          { profileImageFile, removeProfileImage }
        );
        if (response.data && response.data.user) {
          setEmployees(prev => prev.map(emp => emp.id === editEmployeeId ? response.data.user : emp));
          setShowAddModal(false);
          setToast('Employee updated successfully!');
          setTimeout(() => setToast(''), 2500);
          handleModalClose();
        } else {
          setToast('Failed to update employee.');
          setTimeout(() => setToast(''), 2500);
        }
      } catch (err: any) {
        setToast(err?.response?.data?.message || 'Failed to update employee.');
        setTimeout(() => setToast(''), 2500);
      }
    }
  };

  return (
    <div className="w-full min-w-0 space-y-6 p-3 sm:p-4 md:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">Team Access</h2>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            onClick={handleAdd}
            className="inline-flex w-full items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg shadow text-sm font-medium transition-colors sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" /> Add team member
          </button>
        </div>
      </div>
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-green-600 text-white px-4 py-2 rounded shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
      {showAddModal && (
        <div className="fixed inset-0 z-40 flex items-end justify-center overflow-y-auto bg-black/40 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="relative w-full max-w-lg max-h-[min(92dvh,calc(100vh-1rem))] animate-fade-in overflow-y-auto rounded-t-2xl bg-white p-0 shadow-2xl dark:bg-gray-900 sm:max-h-[90vh] sm:rounded-2xl scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-primary-400 dark:scrollbar-thumb-primary-700 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
            <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-primary-600 to-primary-400 px-4 py-4 dark:from-primary-700 dark:to-primary-500 sm:px-8 sm:py-5">
              <h3 className="text-xl font-bold text-white">{modalMode === 'add' ? 'Add team member' : 'Edit team member'}</h3>
              <button
                className="text-white hover:text-gray-200 focus:outline-none"
                onClick={handleModalClose}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form className="space-y-5 px-4 py-5 sm:px-8 sm:py-7" onSubmit={handleAddEmployee}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all px-4 py-2"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all px-4 py-2"
                  placeholder="Enter email address"
                />
              </div>
              {modalMode === 'add' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleFormChange}
                    required
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all px-4 py-2"
                    placeholder="Create a password"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all px-4 py-2"
                >
                  {roleOptions.map(role => (
                    <option key={role} value={role}>{role.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                <select
                  name="department"
                  value={form.department}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all px-4 py-2"
                >
                  {departmentOptions.map(dep => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all px-4 py-2"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Profile Picture</label>
                <div className="flex items-center gap-4">
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      alt="Profile preview"
                      className="h-16 w-16 rounded-full object-cover border border-gray-300 dark:border-gray-600"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs">
                      No photo
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImagePick}
                      className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900/40 dark:file:text-primary-300"
                    />
                    {profilePreview && (
                      <button
                        type="button"
                        onClick={clearProfileImage}
                        className="text-sm text-red-600 dark:text-red-400 hover:underline text-left"
                      >
                        Remove photo
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Qualification</label>
                <input
                  type="text"
                  name="qualification"
                  value={form.qualification}
                  onChange={handleFormChange}
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all px-4 py-2"
                  placeholder="e.g. MBA, B.Tech, Certified Accountant"
                />
              </div>
              <button
                type="submit"
                className="w-full py-2 px-4 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white rounded-lg font-semibold shadow-md transition-all text-base tracking-wide mt-2"
              >
                {modalMode === 'add' ? 'Add team member' : 'Update team member'}
              </button>
            </form>
          </div>
        </div>
      )}
      {showDeleteModal && employeeToDelete && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="w-full max-w-md animate-fade-in rounded-t-xl bg-white p-0 shadow-2xl dark:bg-gray-900 sm:rounded-xl">
            <div className="flex flex-col items-center px-4 py-6 sm:px-8">
              <X className="w-10 h-10 text-red-500 mb-2" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Remove team member</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6 text-center">
                Are you sure you want to permanently delete <span className="font-semibold">{employeeToDelete.name}</span>?<br />This action cannot be undone.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={cancelDelete}
                  className="px-5 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-5 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition shadow"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="ma-table-scroll scrollbar-light rounded-lg bg-white shadow dark:bg-gray-800">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-gray-500 dark:text-gray-400 text-lg">Loading team members...</span>
          </div>
        ) : (
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Photo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Qualification</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {employees.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No team members found.</td>
              </tr>
            ) : (
              employees.map(emp => (
                <tr
                  key={emp.id}
                  className="cursor-pointer transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/30"
                  onClick={() => handleView(emp)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {emp.profile_image ? (
                      <img
                        src={emp.profile_image}
                        alt={emp.name}
                        className="h-10 w-10 rounded-full object-cover border border-gray-200 dark:border-gray-600"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 text-xs font-semibold">
                        {(emp.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{emp.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{emp.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{emp.qualification || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{emp.department}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{emp.role}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{emp.phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex space-x-2">
                      <button onClick={e => { e.stopPropagation(); handleView(emp); }} className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                      <button onClick={e => { e.stopPropagation(); handleEdit(emp); }} className="p-1 text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                      {emp.role !== 'MASTER_ADMIN' && (
                        <button onClick={e => { e.stopPropagation(); handleDelete(emp); }} className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors" title="Delete"><Trash className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
};

export default EmployeesPage; 